import React, { Dispatch, SetStateAction, useContext, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { IdentifyUsernameError } from "iam-client/dist/client/password-reset/passwordReset";
import { OnIdentifyUsernameRequiredApi } from "iam-client/dist/wrapper/resetPasswordFlow";
import { useMessageSource } from "react-message-source";
import Email from "../../../pages/shared/components/email";
import ApplicationPageContent from "../../../ui/layout/page/content/application";
import ContentFooter from "../../../ui/layout/page/content/footer";
import NavigationBar from "../../../ui/layout/page/content/footer/navigation-bar";
import FormContainer from "../../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import TypographyContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import TypographyItemContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import CustomTypography from "../../../ui/widgets/typography";
import { displayErrorInField } from "../../../util/formik";
import { ErrorContext } from "../../error/provider";
import usePasswordResetUsernameFormValidationScheme from "./validator";
import { LanguageContext } from "../../../context/languageContext";

import logging from "../../../util/logging";
import { useNavigate } from "react-router-dom";
const log = logging("username form page");

type FormData = {
  email: string;
};

export default function PasswordResetUsernameForm({
  onNext,
  setEmail,
}: {
  onNext: OnIdentifyUsernameRequiredApi;
  setEmail: Dispatch<SetStateAction<string>>;
}): React.JSX.Element {
  const { language } = useContext(LanguageContext);

  const navigate = useNavigate();

  const formErrors: IdentifyUsernameError[] = onNext && onNext.errors;
  const { getMessage } = useMessageSource();

  const [formikActions, setFormikActions] = useState<FormikHelpers<FormData>>();

  const { setError: setGlobalError } = useContext(ErrorContext);

  const next = (values: FormData, actions: FormikHelpers<FormData>) => {
    setEmail(values.email);
    if (onNext) {
      onNext.proceed({ username: values.email });
    } else {
      log.error(
        "New password flow: Cannot proceed in password reset username form, due to a missing callback",
      );
    }
    setFormikActions(actions);
  };

  if (formErrors) {
    if (formikActions) {
      displayErrorInField(
        formikActions,
        "email",
        getMessage("PasswordReset.ErrorOnAirlock"),
      );
    } else {
      setGlobalError(formErrors);
    }
  }
  const previous = () => {
    navigate("/login");
  };

  return (
    <ApplicationPageContent>
      <TypographyContainer>
        <TypographyItemContainer>
          <CustomTypography variant="h3">
            {getMessage("PasswordReset.forgotPassword")}
          </CustomTypography>
          <CustomTypography variant="body1">
            {getMessage("PasswordReset.pleaseEnterEmail")}
          </CustomTypography>
        </TypographyItemContainer>
      </TypographyContainer>
      <Formik
        key={language}
        initialValues={{ email: "" }}
        validationSchema={usePasswordResetUsernameFormValidationScheme()}
        onSubmit={next}
      >
        {({ values, handleSubmit }) => {
          return (
            <Form>
              <FormContainer>
                <InputContainer>
                  <Email
                    forceReRender
                    name="email"
                    value={values.email}
                    isPrivate={true}
                  />
                </InputContainer>
              </FormContainer>
              <ContentFooter>
                <NavigationBar
                  backButtonText={getMessage("Onboarding.cancel")}
                  nextButtonText={getMessage("Onboarding.continue")}
                  onBack={previous}
                  onNext={handleSubmit}
                />
              </ContentFooter>
            </Form>
          );
        }}
      </Formik>
    </ApplicationPageContent>
  );
}
